import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Gift, Mail, Phone, User, MapPin, Sparkles, MailCheck, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { hasStoredUserToken } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POPUP_DELAY = 4000; // 4 seconds
const POPUP_RESHOW_DAYS = 7; // Show again after 7 days if not registered

// Global flag to prevent double-mounting issue in Strict Mode
let popupTimerStarted = false;

const WelcomePopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSendingLoginLink, setIsSendingLoginLink] = useState(false);
  const [loginLinkSent, setLoginLinkSent] = useState(false);
  const timerRef = useRef(null);
  const cooldownRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    marketing_consent: true
  });

  useEffect(() => {
    // Prevent double-start from Strict Mode
    if (popupTimerStarted) return;

    // En la página de confirmación de correo la persona ya se registró; la sesión
    // llega hasta que responde la verificación, así que aquí ni se arranca el timer.
    if (location.pathname.startsWith('/verificar-email') || location.pathname.startsWith('/entrar/')) return;
    
    // Check if we should show the popup
    const shouldShow = checkShouldShowPopup();
    
    if (shouldShow) {
      popupTimerStarted = true;
      timerRef.current = setTimeout(() => {
        // Volver a revisar: en estos segundos pudo iniciar sesión o confirmar su correo
        if (checkShouldShowPopup()) setIsOpen(true);
      }, POPUP_DELAY);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for custom event to open popup from outside (e.g., navbar)
  useEffect(() => {
    const handleOpenPopup = () => {
      if (hasStoredUserToken()) return; // already logged in
      setIsOpen(true);
    };
    
    window.addEventListener('openWelcomePopup', handleOpenPopup);
    return () => window.removeEventListener('openWelcomePopup', handleOpenPopup);
  }, []);

  const checkShouldShowPopup = () => {
    // Already has an active session (logged in via /login, not just via this
    // popup) — read the token straight from storage, not `isAuthenticated`,
    // since that's only known once AuthContext's async session check resolves.
    if (hasStoredUserToken()) return false;

    // Check if user is already registered (or has logged in / confirmed su correo
    // en este navegador, aunque no haya sido desde este popup)
    const registered = localStorage.getItem('alphatica_registered');
    if (registered === 'true') return false;
    if (localStorage.getItem('alphatica_customer_email')) return false;
    
    // Check if popup was dismissed
    const dismissedAt = localStorage.getItem('alphatica_popup_dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const now = new Date();
      const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      
      // Show again after POPUP_RESHOW_DAYS days
      if (daysSinceDismissed < POPUP_RESHOW_DAYS) {
        return false;
      }
    }
    
    return true;
  };

  const handleClose = () => {
    setIsOpen(false);
    // Store dismissal timestamp
    localStorage.setItem('alphatica_popup_dismissed', new Date().toISOString());
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/public/customers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Mark as registered
        localStorage.setItem('alphatica_registered', 'true');
        localStorage.setItem('alphatica_customer_email', data.email);

        if (data.account_exists && data.email_verified) {
          // An already-verified account owns this email — don't re-register; email
          // them a login link right away instead. If the
          // account exists but ISN'T verified yet, this is most likely the same
          // person continuing their own registration (e.g. closed the tab before
          // confirming) — the backend already resent the verification email for
          // that case, so fall through to the normal "check your email" state.
          setAccountExists(true);
          handleSendLoginLink();
        } else {
          // Show "check your email" state — the coupon isn't usable until confirmed
          setShowSuccess(true);
          setResendCooldown(60);
        }
      } else {
        toast.error(apiErrorMessage(data, 'Error al registrarse'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (isResending || resendCooldown > 0) return;
    setIsResending(true);
    try {
      const response = await fetch(`${API_URL}/api/public/customers/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Correo reenviado');
        setResendCooldown(60);
      } else {
        toast.error(apiErrorMessage(data, 'No se pudo reenviar el correo'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsResending(false);
    }
  };

  const handleSendLoginLink = async () => {
    if (isSendingLoginLink) return;
    setIsSendingLoginLink(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (response.ok) {
        setLoginLinkSent(true);
        setResendCooldown(60);
      } else if (response.status === 429) {
        // Ya se mandó uno hace menos de un minuto: ese enlace sigue sirviendo
        setLoginLinkSent(true);
        setResendCooldown(60);
      } else {
        toast.error(apiErrorMessage(data, 'No se pudo enviar el enlace'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsSendingLoginLink(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownRef.current = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(cooldownRef.current);
  }, [resendCooldown]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div 
        className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        style={{ 
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(27, 79, 216, 0.2)'
        }}
        data-testid="welcome-popup"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-brand-black/60 hover:text-brand-black transition-colors z-10"
          data-testid="welcome-popup-close"
        >
          <X size={20} />
        </button>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #1B4FD8, #C2185B, #1B4FD8)' }} />
        <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(194, 24, 91, 0.15)' }} />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(27, 79, 216, 0.15)' }} />

        {accountExists ? (
          /* Existing account state — no re-registration; a login link is emailed right away */
          <div className="relative p-8 text-center" data-testid="welcome-account-exists">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1B4FD8, #C2185B)' }}
            >
              {loginLinkSent ? <MailCheck size={40} className="text-white" /> : <LogIn size={40} className="text-white" />}
            </div>

            <h3 className="text-2xl font-bold text-brand-black mb-2">
              Ya tienes una cuenta
            </h3>
            <p className="text-brand-black/70 mb-6">
              {isSendingLoginLink && !loginLinkSent ? (
                <>Ya existe una cuenta con <span className="font-semibold">{formData.email}</span>. Enviándote un enlace para entrar...</>
              ) : loginLinkSent ? (
                <>Ya existe una cuenta con <span className="font-semibold">{formData.email}</span>. Te enviamos un enlace a ese correo para iniciar sesión, sin contraseña.</>
              ) : (
                <>Ya existe una cuenta con <span className="font-semibold">{formData.email}</span>. Te podemos enviar un enlace a ese correo para iniciar sesión.</>
              )}
            </p>

            <button
              type="button"
              onClick={handleSendLoginLink}
              disabled={isSendingLoginLink || resendCooldown > 0}
              className={loginLinkSent
                ? 'text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:underline'
                : 'w-full py-3 text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50'}
              style={loginLinkSent ? { color: '#1B4FD8' } : { background: 'linear-gradient(90deg, #1B4FD8, #C2185B)' }}
              data-testid="welcome-send-login-link"
            >
              {isSendingLoginLink
                ? 'Enviando...'
                : !loginLinkSent
                  ? 'Enviarme el enlace para entrar'
                  : resendCooldown > 0
                    ? `Reenviar enlace (${resendCooldown}s)`
                    : '¿No te llegó? Reenviar enlace'}
            </button>
          </div>
        ) : showSuccess ? (
          /* Pending verification state */
          <div className="relative p-8 text-center">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1B4FD8, #C2185B)' }}
            >
              <MailCheck size={40} className="text-white" />
            </div>

            <h3 className="text-2xl font-bold text-brand-black mb-2">
              ¡Ya casi!
            </h3>
            <p className="text-brand-black/70 mb-6">
              Te enviamos un correo a <span className="font-semibold">{formData.email}</span> para confirmar tu cuenta
            </p>

            <div
              className="rounded-xl p-6 mb-4"
              style={{ backgroundColor: '#F5F0E8', border: '1px solid rgba(27, 79, 216, 0.2)' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Gift className="text-brand-magenta" size={24} />
                <span className="text-lg font-bold" style={{ color: '#1B4FD8' }}>
                  10% de descuento
                </span>
              </div>
              <p className="text-brand-black/50 text-xs mt-2">
                Se activa en cuanto confirmes tu correo desde el enlace que te enviamos
              </p>
            </div>

            <button
              type="button"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className="text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:underline"
              style={{ color: '#1B4FD8' }}
            >
              {resendCooldown > 0
                ? `Reenviar correo (${resendCooldown}s)`
                : isResending
                  ? 'Reenviando...'
                  : '¿No te llegó? Reenviar correo'}
            </button>
          </div>
        ) : (
          /* Registration Form */
          <div className="relative p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div 
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
                style={{ background: 'linear-gradient(135deg, #1B4FD8, #C2185B)' }}
              >
                <Sparkles size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-brand-black mb-2">
                ¡Obtén 10% de Descuento!
              </h3>
              <p className="text-brand-black/60 text-sm">
                Regístrate y recibe un cupón exclusivo para tu primera compra
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-cobalt/50" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt transition-colors"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(27, 79, 216, 0.2)' }}
                  data-testid="welcome-name-input"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-cobalt/50" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt transition-colors"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(27, 79, 216, 0.2)' }}
                  data-testid="welcome-email-input"
                />
              </div>

              {/* Phone */}
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-cobalt/50" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Tu teléfono (10 dígitos)"
                  required
                  minLength={10}
                  maxLength={15}
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt transition-colors"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(27, 79, 216, 0.2)' }}
                  data-testid="welcome-phone-input"
                />
              </div>

              {/* City */}
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-cobalt/50" />
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Tu ciudad"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt transition-colors"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(27, 79, 216, 0.2)' }}
                  data-testid="welcome-city-input"
                />
              </div>

              {/* Marketing consent */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="marketing_consent"
                  checked={formData.marketing_consent}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 rounded"
                />
                <span className="text-xs text-brand-black/50">
                  Acepto recibir promociones, novedades y ofertas exclusivas por email y WhatsApp
                </span>
              </label>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 text-white font-bold rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #1B4FD8, #C2185B)' }}
                data-testid="welcome-submit-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Gift size={18} />
                    Obtener mi Cupón
                  </>
                )}
              </button>
            </form>

            {/* Footer note */}
            <p className="text-center text-brand-black/30 text-xs mt-4">
              Al registrarte aceptas nuestros términos y condiciones
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePopup;
